import { useState } from "react";
import type { PopulationSegment } from "@models/populationSegment.model";
import type { SegmentLinkItem, SegmentLinksOrphans } from "@services/engagements/commands/cmdGetSegmentLinks";
import { EngagementService } from "@services/engagements/engagementService";
import { IngestDefinitionService } from "@services/ingest/ingestDefinitionService";
import LinkToPopulationModal, { LinkTargetKind } from "./LinkToPopulationModal";

type Props = {
  orphans: SegmentLinksOrphans;
  segments: readonly PopulationSegment[];
  /** A link was written — the host refetches and re-derives everything. */
  onChanged: () => void;
};

/*
    Orphans can't appear on any population's page — an unlinked entity belongs
    to no population — so the list page is the one place they get flushed out.
    Errors (unroutable specs) come before the
    info-level unset campaigns, which are sometimes legitimately permanent.
*/
export default function LinkIssuesBanner({ orphans, segments, onChanged }: Props) {
  const [target, setTarget] = useState<{ kind: LinkTargetKind; item: SegmentLinkItem } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const specs = orphans.ingest_definitions_without_segment;
  const campaigns = orphans.launches_without_segment;

  if (specs.length === 0 && campaigns.length === 0) return null;

  const handleSave = async (segmentId: number | null, explicitNone: boolean) => {
    if (!target) return;
    setIsSaving(true);
    setError(null);
    try {
      if (target.kind === "campaign") {
        await new EngagementService().setLaunchPopulationSegment(
          target.item.id,
          segmentId,
          explicitNone,
        );
      } else {
        await new IngestDefinitionService().setPopulationSegment(target.item.id, segmentId);
      }
      setTarget(null);
      onChanged();
    } catch {
      setError("Could not save the link. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const row = (key: string, icon: string, body: React.ReactNode, action: React.ReactNode) => (
    <div key={key} className="d-flex align-items-center gap-2 py-1">
      <i className={`bi ${icon}`} />
      <div className="flex-grow-1 small">{body}</div>
      {action}
    </div>
  );

  return (
    <>
      {specs.length > 0 && (
        <div className="alert alert-danger py-2 mb-2">
          {specs.map((spec) =>
            row(
              `spec-${spec.id}`,
              "bi-exclamation-triangle-fill",
              <>
                Data spec <strong>{spec.name}</strong> isn't linked to a population — its files
                can't be routed.
              </>,
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                style={{ fontSize: "0.72rem" }}
                onClick={() => setTarget({ kind: "spec", item: spec })}
              >
                Link…
              </button>,
            ),
          )}
        </div>
      )}

      {campaigns.length > 0 && (
        <div className="alert alert-secondary py-2 mb-2">
          {campaigns.map((campaign) =>
            row(
              `campaign-${campaign.id}`,
              "bi-info-circle",
              <>
                Campaign <strong>{campaign.name}</strong> has no population. Fine for lookalike /
                DB-fed campaigns — set that explicitly so it stops showing here.
              </>,
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: "0.72rem" }}
                onClick={() => setTarget({ kind: "campaign", item: campaign })}
              >
                Set population…
              </button>,
            ),
          )}
        </div>
      )}

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {target && (
        <LinkToPopulationModal
          kind={target.kind}
          entityName={target.item.name}
          segments={segments}
          isSaving={isSaving}
          onClose={() => setTarget(null)}
          onSave={handleSave}
        />
      )}
    </>
  );
}
